// @flow

import React, { Component } from 'react';
import Relay from 'react-relay';
import moment from 'moment';
import { observer } from 'mobx-react';

import ListContainer from './ListContainer';
import { SchedulerComponent, CalendarOverview } from '../Components/index';
import getLanguage from '../libs/Languages';
import { uiStateStoreInstance } from '../Stores/UIStateStore';
import { prepareSchedulerParams } from '../libs/RelayRoutes';


const startToday = moment().startOf('day').add(1, 'second').unix();
const endToday = moment().endOf('day').unix();

const uiState = {
  startDate: startToday,
  endDate: endToday,
};

@observer
class SchedulerContainer extends Component {
  constructor(props) {
    super(props);
    uiStateStoreInstance.setEmails(this.props.relay.variables.emails);
    uiStateStoreInstance.activeView = 'scheduler';
  }

  state = {
    showContactInfoMobile: false,
    calendarView: 'listDay',
  };

  state: {
    showContactInfoMobile: boolean,
    calendarView: string,
  };

  props: {
    viewer: Object,
    relay: Object,
    route: Object,
  };

  _handleContactSelection(operation: string) {
    if (window.innerWidth <= 992) {
      this.setState({
        showContactInfoMobile: true,
      });
    }
    this.props.relay.setVariables({
      emails: uiStateStoreInstance.emails.slice(),
    }, readyState => {
      if (readyState.done || readyState.aborted) {
        if (operation === 'remove') uiStateStoreInstance.events = [];
      } else if (readyState.error) {
        if (operation === 'remove') uiStateStoreInstance.events = [];
      }
    });
  }

  _toggleContactInfoMobile() {
    this.setState({
      showContactInfoMobile: false,
    });
  }

  _forceFetch = () => {
    this.props.relay.forceFetch();
  }

  _schedulerUIStateSetDateRange = (startDate: any, endDate: any) => {
    const nextStartDate = startDate.add(1, 'second').unix();
    const nextEndDate = endDate.unix();
    uiState.startDate = nextStartDate;
    uiState.endDate = nextEndDate;
    this.props.relay.setVariables({
      startDate: startDate.add(1, 'second').unix(),
      endDate: endDate.unix(),
    });
  }

  _schedulerUIStateChangeSelectedTo = (emails: Array<string>) => {
    this.props.relay.setVariables({
      emails,
    });
  }

  _scrollCalendar = (direction: string) => {
    let nextStartDate;
    let nextEndDate;
    const day = direction === 'next' ? 1 : -1;
    if (this.state.calendarView === 'listDay') {
      nextStartDate = moment.unix(this.props.relay.variables.startDate).add('day', day) / 1000;
      nextEndDate = moment.unix(this.props.relay.variables.endDate).add('day', day) / 1000;
    } else {
      nextStartDate = moment.unix(this.props.relay.variables.startDate).add('week', day) / 1000;
      nextEndDate = moment.unix(this.props.relay.variables.endDate).add('week', day) / 1000;
    }
    uiState.startDate = nextStartDate;
    uiState.endDate = nextEndDate;
    this.props.relay.setVariables({
      startDate: nextStartDate,
      endDate: nextEndDate,
    }, readyState => {
      if (readyState.done || readyState.aborted) {
        uiStateStoreInstance.events = [];
      } else if (readyState.error) {
        uiStateStoreInstance.events = [];
      }
    });
  }

  _getInterval = (view: string) => {
    const dayInterval = {
      nextStartDate: moment().startOf('day').add(1, 'second').unix(),
      nextEndDate: moment().endOf('day').unix(),
    };

    const weekInterval = {
      nextStartDate: moment().startOf('week').add(1, 'second').unix(),
      nextEndDate: moment().endOf('week').unix(),
    };

    const interval = view === 'listDay' ? dayInterval : weekInterval;

    return interval;
  }

  _setCalendarView = (view: string) => {
    let nextStartDate;
    let nextEndDate;
    if (view === 'today') {
      const nextInterval = this._getInterval(this.state.calendarView);
      nextStartDate = nextInterval.nextStartDate;
      nextEndDate = nextInterval.nextEndDate;
    } else {
      const nextInterval = this._getInterval(view);
      nextStartDate = nextInterval.nextStartDate;
      nextEndDate = nextInterval.nextEndDate;
      this.setState({
        calendarView: view,
      });
    }

    uiState.startDate = nextStartDate;
    uiState.endDate = nextEndDate;
    this.props.relay.setVariables({
      startDate: nextStartDate,
      endDate: nextEndDate,
      emails: uiStateStoreInstance.emails.slice(),
    }, readyState => {
      if (readyState.done || readyState.aborted) {
        uiStateStoreInstance.events = [];
      } else if (readyState.error) {
        uiStateStoreInstance.events = [];
      }
    });
  }

  render() {
    const { view } = this.props.route;
    return (
      <div>
        <ListContainer
          mode="multi"
          defaultTab="contacts"
          messages={getLanguage()}
          viewer={this.props.viewer}
          selectedContact={this.props.relay.variables.emails}
          toggleItem={(contactEmail) => {
            this._handleContactSelection(contactEmail);
          }}
          languagedata={getLanguage()}
        />
        { view === 'scheduler' ?
          <SchedulerComponent
            viewer={this.props.viewer}
            emails={this.props.relay.variables.emails}
            startDate={this.props.relay.variables.startDate}
            endDate={this.props.relay.variables.endDate}
            languagedata={getLanguage()}
            forceFetch={this._forceFetch}
            setDateRange={this._schedulerUIStateSetDateRange}
            changeSelectedTo={this._schedulerUIStateChangeSelectedTo}
            showContactInfoMobile={this.state.showContactInfoMobile}
            toggleContactInfoMobile={() => { this._toggleContactInfoMobile(); }}
          /> :
            <CalendarOverview
              viewer={this.props.viewer}
              emails={this.props.relay.variables.emails}
              startDate={this.props.relay.variables.startDate}
              endDate={this.props.relay.variables.endDate}
              languagedata={getLanguage()}
              scrollCalendar={this._scrollCalendar}
              setCalendarView={this._setCalendarView}
              calendarView={this.state.calendarView}
            />}
      </div>
    );
  }

}


export default Relay.createContainer(SchedulerContainer, {
  initialVariables: {
    selectedContactEmail: null,
    emails: [null],
    startDate: null,
    endDate: null,
  },
  prepareVariables: vars => {
    let emails = vars.emails;
    // type connections do not accept null params
    // get user email from url

    const userEmail = prepareSchedulerParams({}, location);
    emails = emails[0] === null ? userEmail.emails : emails;
    const startDate = uiState.startDate;
    const endDate = uiState.endDate;

    const preparedVars = {
      ...vars,
      startDate,
      endDate,
      emails,
    };
    return preparedVars;
  },
  fragments: {
    viewer: (vars) => Relay.QL`
      fragment on Viewer {
        ${ListContainer.getFragment('viewer', vars)},
        ${SchedulerComponent.getFragment('viewer', vars)}
        ${CalendarOverview.getFragment('viewer', vars)}
      }
    `,
  },
});



// WEBPACK FOOTER //
// ./src/js/Containers/SchedulerContainer.js